import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, CircularProgress, ToggleButton, ToggleButtonGroup, alpha, useTheme } from '@mui/material';
import { Map as StreetsIcon, SatelliteAltOutlined as SatelliteIcon, RouteOutlined as RouteIcon } from '@mui/icons-material';
import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import Map, { Layer, NavigationControl, Source, type MapLayerMouseEvent } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { APP_SHAPE } from '../../constants/shape';
import { mapStyleFor, type MapMode } from '../../data/mapStyles';
import type { SupplierCollectionPoint } from '../../types/supplier.type';

const RIYADH_VIEW = { longitude: 46.6753, latitude: 24.7136, zoom: 11 };
const AVAILABLE_COLOR = '#94A3B8';
const DEFAULT_SELECTED_COLOR = '#F4841A';
const FALLBACK_COLOR = '#94A3B8';
const FACILITY_COLOR = '#1E293B';

interface FacilityMarker {
  name: string;
  latitude: number | null;
  longitude: number | null;
}

function hasCoords(facility?: FacilityMarker | null): facility is FacilityMarker & { latitude: number; longitude: number } {
  return Boolean(facility) && Number.isFinite(facility!.latitude) && Number.isFinite(facility!.longitude);
}

interface RouteBuilderMapProps {
  availablePoints?: SupplierCollectionPoint[];
  selectedPoints: SupplierCollectionPoint[];
  /** Omit for a read-only map (e.g. a saved route's detail view) - disables click-to-add/remove. */
  onToggle?: (pointId: string) => void;
  /** The route's start/end facility, drawn as a distinct marker and included as the path's true endpoints. */
  startFacility?: FacilityMarker | null;
  endFacility?: FacilityMarker | null;
  /** Real road-following geometry from the routing service, when available. */
  routedGeometry: LineString | null;
  routeLoading?: boolean;
  routeFailed?: boolean;
  /** This route's color - stops, path, and numbered markers all render in it. */
  color?: string;
  height?: number | string;
}

export default function RouteBuilderMap({
  availablePoints = [],
  selectedPoints,
  onToggle,
  startFacility,
  endFacility,
  routedGeometry,
  routeLoading = false,
  routeFailed = false,
  color = DEFAULT_SELECTED_COLOR,
  height = 520,
}: RouteBuilderMapProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mapMode, setMapMode] = useState<MapMode>('streets');

  const located = useCallback(
    (points: SupplierCollectionPoint[]) => points.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)),
    [],
  );

  const facilityFeatures = useMemo((): FeatureCollection<Point> => {
    const features: Feature<Point>[] = [];
    if (hasCoords(startFacility)) {
      features.push({
        type: 'Feature',
        properties: { role: 'start', label: 'S', name: startFacility.name },
        geometry: { type: 'Point', coordinates: [startFacility.longitude, startFacility.latitude] },
      });
    }
    if (hasCoords(endFacility)) {
      features.push({
        type: 'Feature',
        properties: { role: 'end', label: 'E', name: endFacility.name },
        geometry: { type: 'Point', coordinates: [endFacility.longitude, endFacility.latitude] },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [startFacility, endFacility]);

  const availableFeatures = useMemo((): FeatureCollection<Point> => ({
    type: 'FeatureCollection',
    features: located(availablePoints).map((point) => ({
      type: 'Feature',
      properties: { id: point.id },
      geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
    })),
  }), [availablePoints, located]);

  const selectedFeatures = useMemo((): FeatureCollection<Point> => ({
    type: 'FeatureCollection',
    features: located(selectedPoints).map((point, index) => ({
      type: 'Feature',
      properties: { id: point.id, order: String(index + 1) },
      geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
    })),
  }), [selectedPoints, located]);

  // Only ever drawn when the routing service could not resolve a real road path - never
  // presented as if it were the actual route (dashed + muted, distinct from the real line).
  const fallbackLine = useMemo((): FeatureCollection<LineString> => {
    if (routedGeometry || !routeFailed) return { type: 'FeatureCollection', features: [] };
    const coords = [
      ...(hasCoords(startFacility) ? [[startFacility.longitude, startFacility.latitude]] : []),
      ...located(selectedPoints).map((point) => [point.longitude, point.latitude]),
      ...(hasCoords(endFacility) ? [[endFacility.longitude, endFacility.latitude]] : []),
    ];
    const feature: Feature<LineString> = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } };
    return { type: 'FeatureCollection', features: coords.length > 1 ? [feature] : [] };
  }, [selectedPoints, located, routedGeometry, routeFailed, startFacility, endFacility]);

  const routedLine = useMemo((): FeatureCollection<LineString> => ({
    type: 'FeatureCollection',
    features: routedGeometry ? [{ type: 'Feature', properties: {}, geometry: routedGeometry }] : [],
  }), [routedGeometry]);

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!onToggle) return;
      const feats = event.target.queryRenderedFeatures(event.point, { layers: ['rb-available', 'rb-selected'] });
      if (!feats.length) return;
      const id = String(feats[0].properties?.id ?? '');
      if (id) onToggle(id);
    },
    [onToggle],
  );

  const onMouseMove = useCallback((event: MapLayerMouseEvent) => {
    if (!onToggle) return;
    const feats = event.target.queryRenderedFeatures(event.point, { layers: ['rb-available', 'rb-selected'] });
    event.target.getCanvas().style.cursor = feats.length ? 'pointer' : '';
  }, [onToggle]);

  return (
    <Box sx={{ position: 'relative', height, minHeight: 360, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
      <Map
        initialViewState={RIYADH_VIEW}
        minZoom={3}
        maxZoom={18}
        mapStyle={mapStyleFor(mapMode)}
        style={{ width: '100%', height: '100%' }}
        attributionControl={{ compact: true }}
        onClick={onClick}
        onMouseMove={onMouseMove}
        interactiveLayerIds={onToggle ? ['rb-available', 'rb-selected'] : []}
      >
        <NavigationControl position="top-right" showCompass={false} />

        <Source id="rb-routed-line" type="geojson" data={routedLine}>
          <Layer
            id="rb-routed-line-layer"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': color, 'line-width': 4 }}
          />
        </Source>

        <Source id="rb-fallback-line" type="geojson" data={fallbackLine}>
          <Layer
            id="rb-fallback-line-layer"
            type="line"
            paint={{ 'line-color': FALLBACK_COLOR, 'line-width': 2.5, 'line-dasharray': [2, 1.5] }}
          />
        </Source>

        <Source id="rb-available-source" type="geojson" data={availableFeatures}>
          <Layer
            id="rb-available"
            type="circle"
            paint={{
              'circle-radius': 6,
              'circle-color': AVAILABLE_COLOR,
              'circle-stroke-width': 2,
              'circle-stroke-color': theme.palette.background.paper,
            }}
          />
        </Source>

        <Source id="rb-selected-source" type="geojson" data={selectedFeatures}>
          <Layer
            id="rb-selected"
            type="circle"
            paint={{
              'circle-radius': 11,
              'circle-color': color,
              'circle-stroke-width': 2,
              'circle-stroke-color': theme.palette.background.paper,
            }}
          />
          <Layer
            id="rb-selected-label"
            type="symbol"
            layout={{
              'text-field': ['get', 'order'],
              'text-size': 11,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            }}
            paint={{ 'text-color': '#ffffff' }}
          />
        </Source>

        <Source id="rb-facilities-source" type="geojson" data={facilityFeatures}>
          <Layer
            id="rb-facilities"
            type="circle"
            paint={{
              'circle-radius': 14,
              'circle-color': FACILITY_COLOR,
              'circle-stroke-width': 3,
              'circle-stroke-color': theme.palette.background.paper,
            }}
          />
          <Layer
            id="rb-facilities-label"
            type="symbol"
            layout={{
              'text-field': ['get', 'label'],
              'text-size': 11,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            }}
            paint={{ 'text-color': '#ffffff' }}
          />
        </Source>
      </Map>

      <Chip
        icon={routeLoading ? <CircularProgress size={14} /> : <RouteIcon />}
        label={
          routeLoading
            ? t('transportation.routeBuilder.map.calculating')
            : routeFailed
              ? t('transportation.routeBuilder.map.approximate')
              : t('transportation.routeBuilder.map.stopCount', { count: selectedPoints.length })
        }
        sx={{
          position: 'absolute',
          top: 12,
          insetInlineStart: 12,
          zIndex: 5,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          borderRadius: `${APP_SHAPE.radiusSm}px`,
        }}
      />

      <ToggleButtonGroup
        size="small"
        value={mapMode}
        exclusive
        onChange={(_, next: MapMode | null) => next && setMapMode(next)}
        sx={{
          position: 'absolute',
          top: 12,
          insetInlineEnd: 52,
          zIndex: 5,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          borderRadius: `${APP_SHAPE.radiusSm}px`,
        }}
      >
        <ToggleButton value="streets" aria-label={t('suppliers.collectionPoints.map.streets')}><StreetsIcon fontSize="small" /></ToggleButton>
        <ToggleButton value="satellite" aria-label={t('suppliers.collectionPoints.map.satellite')}><SatelliteIcon fontSize="small" /></ToggleButton>
      </ToggleButtonGroup>

      {onToggle && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            insetInlineStart: 12,
            zIndex: 5,
            px: 1.5,
            py: 1,
            borderRadius: `${APP_SHAPE.radiusSm}px`,
            bgcolor: alpha(theme.palette.background.paper, 0.94),
          }}
        >
          {t('transportation.routeBuilder.map.hint')}
        </Box>
      )}
    </Box>
  );
}
